"use client";

import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Modal, Space, Spin } from "@arco-design/web-react";
import "./index.scss";
import { ResponseData, TSamplesCheckResponse } from "@/typings";
import { EProductCheckSampleType, RouteStack } from "@/shared";
import { replacePlaceholdersWithRegex } from "@/utils/regex";
import {
  IconCheckCircleFill,
  IconCloseCircleFill,
} from "@arco-design/web-react/icon";
import { useProductsHighPassRecommend } from "@/api/product/use-query";
import Image from "next/image";
import { useRouter } from "next/navigation";

interface PlatformCheckSampleModalProps {
  visible: boolean;
  onCancel: () => void;
  sampleCheck: ResponseData<TSamplesCheckResponse>;
}

const checkCodes = [
  EProductCheckSampleType.Liveness,
  EProductCheckSampleType.SellerProduct,
];

const PlatformCheckSampleModal: React.FC<PlatformCheckSampleModalProps> = (
  props
) => {
  const { visible, onCancel, sampleCheck } = props;

  const router = useRouter();

  const {
    mutate: fetchProductsHighPassRecommend,
    data: productsHighPassRecommend,
    isLoading,
  } = useProductsHighPassRecommend();

  useEffect(() => {
    if (sampleCheck?.code && checkCodes.includes(sampleCheck?.code)) {
      fetchProductsHighPassRecommend({});
    }
  }, [sampleCheck?.code, fetchProductsHighPassRecommend]);

  const handleLowPrice = useCallback(() => {
    onCancel();
    router.push(`/${RouteStack.LowPrice}`);
  }, [onCancel, router]);

  const requirementItemChecks = useMemo(() => {
    if (sampleCheck?.code && checkCodes.includes(sampleCheck?.code)) {
      return sampleCheck?.data.requirement_item_check;
    }
    return [];
  }, [sampleCheck]);

  const permanentBlack = useMemo(() => {
    const codes = [
      EProductCheckSampleType.PermanentBlack,
      EProductCheckSampleType.PermanentBlackUnrestricted,
    ];
    if (sampleCheck?.code && codes.includes(sampleCheck?.code)) {
      return sampleCheck?.data?.permanent_black;
    }
    return undefined;
  }, [sampleCheck]);

  const productsElement = useMemo(() => {
    if (productsHighPassRecommend?.data) {
      return productsHighPassRecommend?.data.map((v) => (
        <Image
          key={v.id}
          className="product-img"
          src={v.cover}
          alt={v.product_name}
          width={0}
          height={0}
        />
      ));
    }
    return null;
  }, [productsHighPassRecommend?.data]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      autoFocus={false}
      focusLock={true}
      className={"yf-scan-code-modal"}
      footer={null}
    >
      <Spin loading={isLoading}>
        {requirementItemChecks?.length > 0 && (
          <div className="yf-requirements-container">
            <h3 className="title">{sampleCheck?.data?.title}</h3>
            <p className="desc">{sampleCheck?.data?.sub_title}</p>
            <ul className="yf-requirements-list">
              {requirementItemChecks.map((v, i) => (
                <li key={v.key} className="yf-requirements-list-item">
                  <div className="requirement-container">
                    <span className="index">{i + 1}</span>
                    <span className="content">
                      <span>
                        {replacePlaceholdersWithRegex(
                          v.requirement.template,
                          v.requirement.data
                        )}
                      </span>
                      {v.passed ? (
                        <IconCheckCircleFill />
                      ) : (
                        <IconCloseCircleFill />
                      )}
                    </span>
                  </div>
                  {!v.passed && v?.metrics && (
                    <p className="metrics-tip">
                      Your current metric is {v?.metrics}
                    </p>
                  )}
                  {v.under_title && (
                    <p className="under-title">{v.under_title}</p>
                  )}
                </li>
              ))}
            </ul>
            {productsElement && (
              <Space className={"yf-products-high-pass-recommend"} size={12}>
                {productsElement}
              </Space>
            )}
            <p className="yf-data-desc">
              Data sourced from TikTok. For any inquiries please contact
              customer support.
            </p>
            <Button className={"yf-more-product-btn"} onClick={handleLowPrice}>
              <p className="text">View more products</p>
              <p className="desc">(Approved rate &gt; 95%)</p>
            </Button>
          </div>
        )}
        {permanentBlack && (
          <div className="yf-permanent-black-container">
            <Image
              className={"yf-permanent-black-img"}
              src={permanentBlack?.icon}
              alt={""}
              width={0}
              height={0}
            />
            <h3 className="title">{permanentBlack?.title}</h3>
            <p className="content">{permanentBlack?.content}</p>
          </div>
        )}
        {!permanentBlack && !requirementItemChecks?.length && (
          <p className="common-msg">{sampleCheck?.msg}</p>
        )}
      </Spin>
    </Modal>
  );
};

export default PlatformCheckSampleModal;
